<script setup lang="ts">
import { computed, useAttrs } from 'vue'

import { useUiStrings } from '../lib/strings'
import type { ButtonVariant, Size } from './types'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    variant?: ButtonVariant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    /** Shows a busy indicator, blocks activation, announces via aria-busy. */
    loading?: boolean
    /**
     * Sentence announced while loading, e.g. "Posting journal". Falls back to
     * the translated generic from the app's string bundle.
     */
    loadingLabel?: string
    /** Square button with no text; `ariaLabel` then becomes mandatory. */
    iconOnly?: boolean
    ariaLabel?: string
    block?: boolean
    /** Render as an anchor. `href` is forwarded through fallthrough attrs. */
    as?: 'button' | 'a'
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    type: 'button',
    loadingLabel: '',
    ariaLabel: '',
    as: 'button',
  },
)

const emit = defineEmits<{ click: [event: MouseEvent] }>()

const attrs = useAttrs()
const strings = useUiStrings()

const isDisabled = computed(() => props.disabled || props.loading)

const busyLabel = computed(() =>
  props.loadingLabel === '' ? strings.value.buttonWorking : props.loadingLabel,
)

const classes = computed(() => [
  'e-btn',
  `e-btn--${props.variant}`,
  `e-btn--${props.size}`,
  { 'e-btn--icon': props.iconOnly, 'e-btn--block': props.block, 'is-loading': props.loading },
])

function onClick(event: MouseEvent): void {
  if (isDisabled.value) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>

<template>
  <component
    :is="as"
    v-bind="attrs"
    :class="classes"
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? isDisabled : undefined"
    :aria-disabled="isDisabled ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="ariaLabel === '' ? undefined : ariaLabel"
    :tabindex="as === 'a' && isDisabled ? -1 : undefined"
    @click="onClick"
  >
    <span v-if="loading" class="e-btn__busy" aria-hidden="true" />
    <span v-if="$slots.icon && !loading" class="e-btn__icon"><slot name="icon" /></span>
    <span v-if="!iconOnly" class="e-btn__label"><slot /></span>
    <span v-if="$slots.trailing && !iconOnly" class="e-btn__icon"><slot name="trailing" /></span>
    <span v-if="loading" class="e-visually-hidden">{{ busyLabel }}</span>
  </component>
</template>

<style>
.e-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--erpx-space-2);
  height: var(--erpx-control-h-lg);
  padding-inline: var(--erpx-space-3);
  border: 1px solid transparent;
  border-radius: var(--erpx-radius);
  font-family: var(--erpx-font-ui);
  font-size: var(--erpx-text-base);
  font-weight: var(--erpx-weight-medium);
  line-height: var(--erpx-leading-none);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  transition:
    background-color var(--erpx-duration-fast) var(--erpx-easing-standard),
    border-color var(--erpx-duration-fast) var(--erpx-easing-standard),
    color var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-btn:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.e-btn--sm {
  height: var(--erpx-control-h);
  padding-inline: var(--erpx-space-2);
  font-size: var(--erpx-text-sm);
  gap: var(--erpx-space-1);
}

.e-btn--block {
  display: flex;
  width: 100%;
}

.e-btn--icon {
  width: var(--erpx-control-h-lg);
  padding-inline: 0;
}

.e-btn--icon.e-btn--sm {
  width: var(--erpx-control-h);
}

.e-btn[disabled],
.e-btn[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.55;
}

/* primary — one per screen region; the affirmative action */
.e-btn--primary {
  background-color: var(--erpx-color-accent-solid);
  color: var(--erpx-color-accent-on-solid);
  border-color: var(--erpx-color-accent-solid);
}

.e-btn--primary:hover:not([disabled]):not([aria-disabled='true']) {
  background-color: var(--erpx-color-accent-solid-hover);
  border-color: var(--erpx-color-accent-solid-hover);
}

.e-btn--primary:active:not([disabled]) {
  background-color: var(--erpx-color-accent-solid-active);
}

/* secondary — the default; bordered, reads as a control not an accent */
.e-btn--secondary {
  background-color: var(--erpx-color-surface-raised);
  color: var(--erpx-color-text-primary);
  border-color: var(--erpx-color-border-default);
}

.e-btn--secondary:hover:not([disabled]):not([aria-disabled='true']) {
  background-color: var(--erpx-color-surface-hover);
  border-color: var(--erpx-color-border-strong);
}

/* ghost — toolbars and table row actions */
.e-btn--ghost {
  background-color: transparent;
  color: var(--erpx-color-text-secondary);
}

.e-btn--ghost:hover:not([disabled]):not([aria-disabled='true']) {
  background-color: var(--erpx-color-surface-hover);
  color: var(--erpx-color-text-primary);
}

/* danger — destructive and irreversible only */
.e-btn--danger {
  background-color: var(--erpx-color-danger-solid);
  color: var(--erpx-color-danger-on-solid);
  border-color: var(--erpx-color-danger-solid);
}

.e-btn--danger:hover:not([disabled]):not([aria-disabled='true']) {
  background-color: var(--erpx-color-danger-solid-hover);
  border-color: var(--erpx-color-danger-solid-hover);
}

.e-btn__icon {
  display: inline-flex;
  align-items: center;
}

.e-btn__busy {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: var(--erpx-radius-full);
  animation: e-btn-spin 600ms linear infinite;
}

@keyframes e-btn-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .e-btn__busy {
    animation-duration: 2400ms;
  }
}

.e-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
